import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap, IUploadFile } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * @slot default - Texto personalizado para o botão de upload. Se não fornecido, será exibido "Selecione o arquivo" como texto padrão.
 *                 Use este slot para personalizar o texto do botão de acordo com o contexto do upload, por exemplo: "Anexar documentos",
 *                 "Enviar imagens", etc.
 * @slot upload-list - Permite customizar a área de listagem de arquivos. Se utilizado, substitui a lista padrão gerada pelo componente.
 */
@Component({
  tag: 'br-upload',
  styleUrl: 'upload.scss',
  shadow: true,
  formAssociated: true,
})
export class Upload {
  /**
   * Identificador único do componente; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('br-upload')
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el!: HTMLBrUploadElement

  /**
   * Tipos de arquivo permitidos (ex.: 'image/*').
   */
  @Prop() accept: string = ''

  /**
   * Rótulo exibido acima do botão de upload.
   */
  @Prop() label: string = 'Envio de arquivo'

  /**
   * Lista de arquivos já enviados (para edição ou pré-visualização).
   * Pode ser uma string JSON ou um array de objetos IUploadFile.
   */
  @Prop() uploadFiles: string | IUploadFile[] = []

  /**
   * Estado interno para armazenar os arquivos processados da prop uploadFiles.
   */
  @State() parsedUploadFiles: IUploadFile[] = []

  /**
   * Evento emitido quando um arquivo é selecionado.
   * Este estado armazena os arquivos que foram selecionados pelo usuário.
   */
  @State() filesSelected: FileList | null = null

  /**
   * Arquivo único quando a seleção não é múltipla.
   */
  @State() file: File | null = null

  /**
   * Desativa interação e seleção de arquivos quando true.
   */
  @Prop({ reflect: true }) disabled: boolean = false

  /**
   * Estado visual: 'info', 'warning', 'danger' ou 'success'.
   */
  @Prop({ reflect: true }) state: 'info' | 'warning' | 'danger' | 'success'

  /**
   * Indica se o componente permite a seleção de múltiplos arquivos.
   * Quando definido como `true`, o usuário pode selecionar mais de um arquivo para upload.
   */
  @Prop() multiple: boolean = false

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Emitido quando a lista de arquivos selecionados muda.
   */
  @Event() selectedFilesChange: EventEmitter<FileList>

  /**
   * Evento emitido quando um arquivo da lista `uploadFiles` (externos) é removido pelo usuário.
   * O objeto emitido contém os dados do arquivo removido.
   */
  @Event() brRemove: EventEmitter<IUploadFile>

  @Watch('uploadFiles')
  protected uploadFilesHandler(newValue: string | IUploadFile[]) {
    if (typeof newValue === 'string') {
      try {
        this.parsedUploadFiles = JSON.parse(newValue)
      } catch (e) {
        console.error('Erro ao fazer parse de uploadFiles:', e)
        this.parsedUploadFiles = []
      }
    } else {
      this.parsedUploadFiles = newValue
    }
  }

  componentWillLoad() {
    this.uploadFilesHandler(this.uploadFiles)
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-upload': true,
      [this.state]: true,
    }
  }

  private handleRemoveFileSelection(file: File | IUploadFile) {
    // Se for um objeto File nativo (novo upload)
    if (file instanceof File) {
      const newArrayFilter = Array.from(this.filesSelected).filter((f) => f !== file)
      this.filesSelected = newArrayFilter as unknown as FileList

      // Limpa o input se não houver mais arquivos
      const fileInput = this.el.shadowRoot.getElementById(`single-file-${this.customId}`) as HTMLInputElement
      if (newArrayFilter.length === 0) {
        fileInput.value = ''
      } else {
        // Nota: FileList é read-only no input, então a manipulação aqui é apenas no estado interno.
        // Para redefinir completamente o input é preciso setar value = ''.
        // Em uploads múltiplos, remover um arquivo específico do input.files não é trivial sem DataTransfer hack,
        // mas o estado visual está atualizado.
      }
      this.selectedFilesChange.emit(this.filesSelected)
    }
    // Se for um arquivo vindo de uploadFiles (externo)
    else {
      this.brRemove.emit(file as IUploadFile)
    }
  }

  private renderFilesUploaded() {
    const newFiles = this.filesSelected ? Array.from(this.filesSelected) : []
    const existingFiles = this.parsedUploadFiles || []

    // Combina as listas para renderização
    const allFiles = [
      ...existingFiles.map((f) => ({ file: f, isNew: false })),
      ...newFiles.map((f) => ({ file: f, isNew: true })),
    ]

    return allFiles.map(({ file, isNew }) => {
      const size = isNew
        ? Helpers.formatBytes((file as File).size)
        : (file as IUploadFile).size
          ? Helpers.formatBytes((file as IUploadFile).size)
          : ''

      return (
        <div class="br-item d-flex" key={file.name} role="listitem">
          <div class="content text-primary-default mr-auto">{file.name}</div>
          <div class="support mr-n2">
            <span class="">{size}</span>
            <br-button
              shape="circle"
              type="button"
              aria-label={`Remover arquivo ${file.name}`}
              onClick={() => this.handleRemoveFileSelection(file)}
            >
              <br-icon icon-name="fa-solid:trash" aria-hidden="true"></br-icon>
            </br-button>
          </div>
        </div>
      )
    })
  }

  private onInputChange(files: FileList) {
    if (files.length === 1) {
      this.filesSelected = files
    } else if (files.length > 0) {
      this.filesSelected = files
    } else {
      console.error(files.length === 0 ? 'NENHUM ARQUIVO ENVIADO' : 'VOCÊ SÓ PODE ENVIAR UM ARQUIVO POR VEZ')

      return false
    }
    this.selectedFilesChange.emit(this.filesSelected)
  }

  // Método antigo não é mais necessário pois usamos Helpers.formatBytes diretamente no render
  // private _calculateFileSize(files: FileList) { ... }

  private handleDrop(event: DragEvent) {
    event.preventDefault()
    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      this.onInputChange(files)
    }
  }

  private handleDragOver(event: DragEvent) {
    event.preventDefault()
  }

  private handleFileSelection() {
    const fileInput = this.el.shadowRoot.getElementById(`single-file-${this.customId}`) as HTMLInputElement
    fileInput.click()
  }

  private readonly handleInputChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    this.onInputChange(target.files)
  }

  render() {
    const hasFiles =
      (this.filesSelected && this.filesSelected.length > 0) ||
      (this.parsedUploadFiles && this.parsedUploadFiles.length > 0)

    return (
      <Host>
        <div class={this.getCssClassMap()} id={this.customId}>
          <label id={`label-${this.customId}`} class="upload-label" htmlFor={`single-file-${this.customId}`}>
            <span>{this.label}</span>
          </label>
          <input
            class="upload-input"
            id={`single-file-${this.customId}`}
            type="file"
            onChange={this.handleInputChange}
            multiple={this.multiple}
            disabled={this.disabled}
            accept={this.accept}
            aria-labelledby={`label-${this.customId}`}
          />
          <button
            class="upload-button"
            type="button"
            disabled={this.disabled}
            onClick={() => this.handleFileSelection()}
            onDrop={(event) => this.handleDrop(event)}
            onDragOver={(event) => this.handleDragOver(event)}
            aria-describedby={`label-${this.customId}`}
          >
            <br-icon icon-name="fa-solid:upload" height="16" aria-hidden="true"></br-icon>
            <slot>
              <span>Selecione o arquivo</span>
            </slot>
          </button>

          <slot name="upload-list">
            {hasFiles && (
              <div id={`${this.customId}-list-files`} class="upload-list" role="list">
                {this.renderFilesUploaded()}
              </div>
            )}
          </slot>
        </div>
      </Host>
    )
  }
}
